/**
 * Shared helpers for the campaign-detail API factories. Used by both
 * `createBrandCampaignDetailApi` and `createAdminCampaignDetailApi`.
 *
 * Both factories follow the same patterns:
 * - paginated lists use `{ data: T[], pagination: {...} }` envelope
 * - single-resource GETs use `{ data: T }` envelope
 * - shared thread endpoints (no role prefix) live under `/roster/...`
 *
 * The default page size is 20 (BE default, per ADR-0001).
 */
import { apiClient, ApiError } from '@discovr/common';
import type { DataResponse, ResolvedAudio } from '@discovr/types';

export type Role = 'brand' | 'admin';

/** Standard return type for every paginated roster fetch. */
export interface PagedResult<T> {
  entries: T[];
  total: number;
  page: number;
  totalPages: number;
}

/** Parse the BE's WritePaginated envelope into `PagedResult`. */
export function parsePaged<T>(body: unknown): PagedResult<T> {
  const b = body as {
    data?: T[];
    entries?: T[];
    pagination?: { total?: number; page?: number; total_pages?: number };
  };
  return {
    entries: b?.data ?? b?.entries ?? [],
    total: b?.pagination?.total ?? 0,
    page: b?.pagination?.page ?? 1,
    totalPages: b?.pagination?.total_pages ?? 1,
  };
}

export function rosterBase(role: Role, id: string, tab: string): string {
  return `/${role}/campaigns/${encodeURIComponent(id)}/roster/${tab}`;
}

export function rosterQs({
  status,
  page = 1,
  limit = 20,
}: {
  status?: string;
  page?: number;
  limit?: number;
} = {}): string {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status) qs.set('status', status);
  return qs.toString();
}

/** Thread types the BE persists per roster entry. */
export type ThreadType = 'selection' | 'script' | 'content';

/**
 * POST a comment to a roster-entry thread. Routes through the unified
 * `/roster/{entry}/threads/{type}/messages` — no role prefix.
 */
export function postThreadComment(
  rosterEntryId: string,
  threadType: ThreadType,
  body: string,
) {
  return apiClient.post(
    `/roster/${encodeURIComponent(rosterEntryId)}/threads/${threadType}/messages`,
    { body },
  );
}

/** Read `?status=` `?from=` `?to=` style query strings off a partial. */
export function dateRangeQs({ fromDate, toDate }: { fromDate?: string; toDate?: string }): string {
  const qs = new URLSearchParams();
  if (fromDate) qs.set('from', fromDate);
  if (toDate) qs.set('to', toDate);
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/** Return null on 404; rethrow on other errors. Used by sentiment endpoints. */
export async function getOrNullOn404<T>(url: string): Promise<T | null> {
  try {
    const res = await apiClient.get<DataResponse<T>>(url);
    return res.data.data ?? null;
  } catch (err) {
    if (err instanceof ApiError && err.httpStatus === 404) return null;
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

/** Per-request axios options (e.g. `timeout`) — derived so features needs no axios import. */
export type GetDataConfig = Parameters<typeof apiClient.get>[1];

/** Read a `DataResponse<T>` envelope; throws on error. */
export async function getData<T>(url: string, config?: GetDataConfig): Promise<T> {
  // No second argument when there is no config, so callers' request shape is unchanged.
  const res = config
    ? await apiClient.get<DataResponse<T>>(url, config)
    : await apiClient.get<DataResponse<T>>(url);
  return res.data.data;
}

/**
 * Resolve an Instagram audio URL to its track/artist/cover metadata (Official
 * Audio Compliance setup). No role prefix — stateless, campaign:read-gated on
 * the BE — so both the brand create wizard and the campaign-edit audio field
 * call this one implementation instead of each hardcoding the endpoint.
 */
export function resolveOfficialAudio(audioUrl: string): Promise<ResolvedAudio> {
  return apiClient
    .post<DataResponse<ResolvedAudio>>('/instagram/audio/resolve', { audio_url: audioUrl })
    .then((r) => r.data.data);
}
